import React from 'react';
import { motion } from 'framer-motion';
import Section from './Section';
import { useSanityContent } from '../hooks/useSanityContent';
import { fetchTrustCore } from '../lib/sanity';
import { defaultTrustCore } from '../lib/defaultContent';
import type { TrustCoreContent } from '../lib/types';

/* ─── individual stat ─────────────────────────────────────── */
const Stat: React.FC<{ value: string; label: string; index: number }> = ({
  value,
  label,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.45, delay: 0.1 + index * 0.1 }}
    className="text-center"
  >
    <div className="text-3xl sm:text-4xl font-playfair font-bold text-primary-black leading-none">
      {value}
    </div>
    <div className="text-[11px] text-gray-400 font-inter uppercase tracking-widest mt-2 leading-snug whitespace-pre-line">
      {label}
    </div>
  </motion.div>
);

/* ─── main component ──────────────────────────────────────── */
const TrustCore: React.FC = () => {
  const { content } = useSanityContent<TrustCoreContent>(fetchTrustCore, defaultTrustCore);
  const stats = content.stats && content.stats.length ? content.stats : defaultTrustCore.stats;
  const centerTop = content.centerTop && content.centerTop.length ? content.centerTop : defaultTrustCore.centerTop;
  const centerBottom = content.centerBottom && content.centerBottom.length ? content.centerBottom : defaultTrustCore.centerBottom;
  return (
    <Section padding="default">
      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8 sm:mb-10"
      >
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-4">
          {content.title}
        </h2>
        <p className="text-sm sm:text-base text-gray-500 max-w-xl mx-auto mb-6 leading-relaxed">
          {content.description}
        </p>
        <a
          href={content.ctaLink}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-black text-white text-sm font-inter font-semibold rounded-lg hover:bg-gray-800 transition-colors"
        >
          {content.ctaText}
        </a>
      </motion.div>

      {/* ── Desktop: radial diagram ── */}
      <div
        className="relative mx-auto hidden md:block my-6"
        style={{ maxWidth: 700, height: 380 }}
      >
        {/* SVG — connecting lines + midpoint dots */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          overflow="visible"
        >
          {/* lines — endpoints calibrated to stat centers */}
          <line x1="12%" y1="17%" x2="50%" y2="50%" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="5 4" />
          <line x1="88%" y1="17%" x2="50%" y2="50%" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="5 4" />
          <line x1="12%" y1="83%" x2="50%" y2="50%" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="5 4" />
          <line x1="88%" y1="83%" x2="50%" y2="50%" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="5 4" />

          {/* midpoint dots */}
          <circle cx="31%" cy="33.5%" r="3.5" fill="#ef4444" fillOpacity="0.6" />
          <circle cx="69%" cy="33.5%" r="3.5" fill="#ef4444" fillOpacity="0.6" />
          <circle cx="31%" cy="66.5%" r="3.5" fill="#ef4444" fillOpacity="0.6" />
          <circle cx="69%" cy="66.5%" r="3.5" fill="#ef4444" fillOpacity="0.6" />

          {/* endpoint nodes — pulled inward along the line so they don't overlap stats */}
          <g>
            <circle cx="21%" cy="24%" r="5" fill="white" stroke="#d1d5db" strokeWidth="1.5" />
            <circle cx="21%" cy="24%" r="2" fill="#9ca3af" />
          </g>
          <g>
            <circle cx="79%" cy="24%" r="5" fill="white" stroke="#d1d5db" strokeWidth="1.5" />
            <circle cx="79%" cy="24%" r="2" fill="#9ca3af" />
          </g>
          <g>
            <circle cx="21%" cy="76%" r="5" fill="white" stroke="#d1d5db" strokeWidth="1.5" />
            <circle cx="21%" cy="76%" r="2" fill="#9ca3af" />
          </g>
          <g>
            <circle cx="79%" cy="76%" r="5" fill="white" stroke="#d1d5db" strokeWidth="1.5" />
            <circle cx="79%" cy="76%" r="2" fill="#9ca3af" />
          </g>
        </svg>

        {/* Stats — absolute at corners */}
        <div className="absolute top-[4%] left-[1%]">
          <Stat {...stats[0]} index={0} />
        </div>
        <div className="absolute top-[4%] right-[1%]">
          <Stat {...stats[1]} index={1} />
        </div>
        <div className="absolute bottom-[4%] left-[1%]">
          <Stat {...stats[2]} index={2} />
        </div>
        <div className="absolute bottom-[4%] right-[1%]">
          <Stat {...stats[3]} index={3} />
        </div>

        {/* Trust Core — center */}
        <motion.div
          initial={{ opacity: 0, scale: 0.75 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
          className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center"
        >
          {/* inner wrapper so rings are relative to the circle, not the full container */}
          <div className="relative flex items-center justify-center">
            {/* pulse ring */}
            <div className="absolute -inset-5 rounded-full border border-primary-red/15 animate-pulse" />
            {/* outer ring */}
            <div className="absolute -inset-2.5 rounded-full border border-gray-100" />

            {/* circle body */}
            <div className="w-40 h-40 rounded-full bg-white border border-gray-200 shadow-md flex flex-col items-center justify-center gap-0">
            {/* top label */}
            <span className="text-[9px] text-primary-red font-inter font-bold uppercase tracking-[0.22em] leading-tight">
              {centerTop[0]}
            </span>
            <span className="text-[9px] text-primary-red font-inter font-bold uppercase tracking-[0.22em] leading-tight">
              {centerTop[1]}
            </span>

            {/* divider */}
            <div className="w-10 h-px bg-gray-200 my-3" />

            {/* brand */}
            <span className="text-[8px] text-gray-400 font-inter uppercase tracking-[0.18em] leading-tight">
              {centerBottom[0]}
            </span>
            <span className="text-[8px] text-gray-400 font-inter uppercase tracking-[0.18em] leading-tight mt-0.5">
              {centerBottom[1]}
            </span>
            </div> {/* circle body */}
          </div> {/* relative wrapper */}
        </motion.div>
      </div>

      {/* ── Mobile: stacked ── */}
      <div className="md:hidden flex flex-col items-center gap-10">
        {/* center circle */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <div className="absolute -inset-3 rounded-full border border-primary-red/15 animate-pulse" />
          <div className="w-32 h-32 rounded-full bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center">
            <span className="text-[9px] text-primary-red font-inter font-bold uppercase tracking-[0.2em] leading-tight">
              {centerTop.join(' ')}
            </span>
            <div className="w-8 h-px bg-gray-200 my-2.5" />
            <span className="text-[8px] text-gray-400 font-inter uppercase tracking-widest">
              {centerBottom.join(' ')}
            </span>
          </div>
        </motion.div>

        {/* 2x2 stats grid */}
        <div className="grid grid-cols-2 gap-x-10 gap-y-8 w-full max-w-xs">
          {stats.map((s, i) => (
            <Stat key={i} {...s} index={i} />
          ))}
        </div>
      </div>
    </Section>
  );
};

export default TrustCore;
